import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';
import type { Contradiction, ContradictionSide } from '@/services/types';

/**
 * One potential inconsistency, side by side.
 *
 * Three product rules are visible in this component rather than assumed:
 *
 * 1. An UNVERIFIED evidence span is never rendered as a citation. The model
 *    quoted it, but it was not found verbatim in the source it was given -- a
 *    user clicking through would fail to find it. Those spans are shown with a
 *    warning and visually demoted, never silently mixed with verified ones.
 * 2. The verdict carries the model and prompt version that produced it. It is an
 *    assertion by a particular model, not a property of the corpus.
 * 3. The language is "potential inconsistency" throughout. Nothing here says a
 *    decision was wrong.
 * 4. "Unverified" is not one state. A span can miss verbatim by punctuation, be
 *    a paraphrase the model attributed as a quote, or never have been checked at
 *    all. The badge names which, because only the last is fixable by rerunning
 *    the pass and only the middle one makes the quotation unusable.
 */
export function ContradictionCard({ item }: { item: Contradiction }) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <article className="space-y-3 rounded-lg border bg-card p-4">
      <header className="space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
            {t('anatel:consistency.potentialInconsistency')}
          </span>
          {item.explanation_category ? (
            <span className="rounded border px-2 py-0.5 text-xs text-muted-foreground">
              {t(`anatel:consistency.categories.${item.explanation_category}`, {
                defaultValue: item.explanation_category,
              })}
            </span>
          ) : null}
          {item.confidence !== null ? (
            <span className="text-xs text-muted-foreground">
              {t('anatel:consistency.confidence', { value: percent.format(item.confidence) })}
            </span>
          ) : null}
          <QaBadge item={item} />
          {item.qa_propositions_corroborated ? (
            // Shown because it is RARE -- 19 of 113 reviewed pairs. Marking the
            // corroborated ones is more useful than marking the majority.
            <span className="rounded border border-emerald-600/40 bg-emerald-600/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-300">
              {t('anatel:consistency.evidence.corroboratedBadge')}
            </span>
          ) : null}
        </div>
        {item.shared_issue ? (
          <h3 className="text-sm font-semibold text-foreground">{item.shared_issue}</h3>
        ) : null}
      </header>

      <div className="grid gap-3 md:grid-cols-2">
        <Side side={item.side_a} label={t('anatel:consistency.sideA')} />
        <Side side={item.side_b} label={t('anatel:consistency.sideB')} />
      </div>

      {item.explanation ? (
        <section className="space-y-1 border-t pt-2">
          <h4 className="text-xs font-medium text-foreground">
            {t('anatel:consistency.whyFlagged')}
          </h4>
          <p className="text-xs text-muted-foreground">{item.explanation}</p>
        </section>
      ) : null}

      {item.shared_citations.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {item.shared_citations.map((citation) => (
            <li key={citation} className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {citation}
            </li>
          ))}
        </ul>
      ) : null}

      {/* The object labels itself: this verdict came from this model under this
          prompt, and a different one would produce a different queue. */}
      <footer className="border-t pt-2 text-[11px] text-muted-foreground">
        {t('anatel:consistency.producedBy', {
          model: item.model_name,
          prompt: item.prompt_version,
        })}
      </footer>
    </article>
  );
}

/**
 * What the QA pass concluded about this pair's quotations.
 *
 * AUTO_VERIFIED renders nothing: it is the expected state, and a badge on 99 of
 * 125 rows is noise that trains the eye to skip the badges that matter.
 */
function QaBadge({ item }: { item: Contradiction }) {
  const { t } = useTranslation(['anatel', 'common']);
  const ratio = item.qa_worst_ratio;

  if (item.qa_status === 'AUTO_VERIFIED') return null;

  if (item.qa_status === 'NOT_QA_REVIEWED') {
    // The base table's own span flags still apply. Absence of a QA record does
    // not upgrade a failed span into an unknown one.
    const label = item.fully_verified
      ? t('anatel:consistency.evidence.badge.NOT_QA_REVIEWED')
      : t('anatel:consistency.evidence.badge.NOT_QA_REVIEWED_UNVERIFIED');
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs',
          item.fully_verified
            ? 'text-muted-foreground'
            : 'border-destructive/40 bg-destructive/10 text-destructive',
        )}
      >
        {item.fully_verified ? null : <AlertTriangle className="h-3 w-3" aria-hidden />}
        {label}
      </span>
    );
  }

  // AUTO_REJECTED is severe, not mild: the span was checked and FAILED. Only the
  // near-exact state earns the softer colour, and anything unrecognised is
  // treated as severe rather than assumed benign.
  const nearExact = item.qa_status === 'AUTO_VERIFIED_FUZZY';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs',
        nearExact
          ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
          : 'border-destructive/40 bg-destructive/10 text-destructive',
      )}
    >
      <AlertTriangle className="h-3 w-3" aria-hidden />
      {t(`anatel:consistency.evidence.badge.${item.qa_status}`, {
        ratio: ratio !== null ? ratio.toFixed(2) : '?',
        defaultValue: item.qa_status,
      })}
    </span>
  );
}

function Side({ side, label }: { side: ContradictionSide; label: string }) {
  const { t } = useTranslation(['anatel']);
  const verified = side.evidence_verified === true;

  return (
    <div className="space-y-2 rounded-md border bg-background p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground">{side.reference_date ?? '—'}</span>
      </div>

      {side.process_key ? (
        <Link
          to={`/anatel/p/${side.process_key}`}
          className="block text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          {side.process_number}
        </Link>
      ) : (
        <span className="text-sm text-foreground">{side.process_number ?? '—'}</span>
      )}

      <div className="flex flex-wrap gap-1">
        {side.outcome_codes.map((code) => (
          <span key={code} className="rounded border px-1.5 py-0.5 text-[11px] text-foreground">
            {t(`anatel:overview.outcomes.${code}`, { defaultValue: code })}
          </span>
        ))}
      </div>

      {side.proposition ? (
        <p className="text-xs text-foreground">{side.proposition}</p>
      ) : null}

      {side.evidence ? (
        <blockquote
          className={cn(
            'border-l-2 pl-2 text-xs italic',
            verified ? 'border-primary/50 text-muted-foreground' : 'border-destructive/50 text-destructive',
          )}
        >
          <span className="mb-1 flex items-center gap-1 not-italic">
            {verified ? (
              <>
                <ShieldCheck className="h-3 w-3" aria-hidden />
                {t('anatel:consistency.evidenceVerified')}
              </>
            ) : (
              <>
                <AlertTriangle className="h-3 w-3" aria-hidden />
                {t('anatel:consistency.evidenceUnverified')}
              </>
            )}
          </span>
          {side.evidence}
        </blockquote>
      ) : null}
    </div>
  );
}
